import { FC } from "react";
import { PieChart, Pie, Legend, PieLabelRenderProps, PieSectorShapeProps, Sector, Tooltip } from "recharts";
import LoadingSpinner from "@app/shared/LoadingSpinner";
import { QESAlert } from "@app/shared/qes-standards/Alert";
import { getGenericErrorMessage } from "@app/shared/utility/getGenericErrorMessage";
import { QESWidget, QESWidgetContent, QESWidgetHeader, QESWidgetTitle } from "../../../shared/widget";
import { QesColors } from "@app/shared/qes-standards/colors";
import { useOpenGaps } from "../hooks/useOpenGaps";

const RADIAN = Math.PI / 180;

const ComplianceConnectOpenGapsWidget: FC = () => {
  const { openGaps, openGapsError, isLoading: loading } = useOpenGaps();

  const pieData = [
    { color: QesColors.QesPrimaryGreen, name: "Fillable Gaps", value: openGaps?.fillableGaps ?? 0 },
    { color: QesColors.QesTertiaryYellow2, name: "Partially Fillable Gaps", value: openGaps?.partiallyFillableGaps ?? 0 },
    { color: QesColors.QesPrimaryRed, name: "Unfillable Gaps", value: openGaps?.unfillableGaps ?? 0 },
  ];

  const totalGaps = pieData.reduce((sum, d) => sum + d.value, 0);
  const emptyData = [{ color: QesColors.QesPrimaryGray4, name: "No Data", value: 1 }];

  const renderLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, value, percent }: PieLabelRenderProps) => {
    if ((percent ?? 0) < 0.05) return null;
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-(midAngle ?? 0) * RADIAN);
    const y = cy + radius * Math.sin(-(midAngle ?? 0) * RADIAN);
    return (
      <text dominantBaseline="central" fill="white" fontWeight="bold" fontSize="0.75rem" textAnchor="middle" x={x} y={y}>
        {value.toLocaleString()}
      </text>
    );
  };

  const renderOpenGapsLegend = () => (
    <div className="d-flex flex-column">
      {pieData.map((d) => (
        <div key={d.name} className="d-flex align-items-center">
          <svg width={13} height={13} viewBox="0 0 32 32" style={{ display: "inline-block", flexShrink: 0, marginRight: 6 }}>
            <rect x={0} y={0} width={32} height={32} fill={d.color} />
          </svg>
          <span style={{ color: QesColors.QesPrimaryGray8 }}>{d.name}</span>
        </div>
      ))}
    </div>
  );

  return (
    <QESWidget className="compliance-connect-open-gaps-widget">
      <QESWidgetHeader>
        <QESWidgetTitle>OPEN GAPS</QESWidgetTitle>
      </QESWidgetHeader>
      <QESWidgetContent>
        {loading
          ? <LoadingSpinner />
          : openGapsError
            ? <QESAlert type="danger" persist>{getGenericErrorMessage()}</QESAlert>
            : <>
              <br />
              <span className="header-s mb-0" style={{ lineHeight: 1 }}>
                {totalGaps?.toLocaleString()}<span style={{ fontWeight: "normal" }}> Open Gaps</span>
              </span>
              <div className="d-flex flex-row align-items-center">
                <PieChart height={150} width={150}>
                  <Legend
                    align="left"
                    layout="vertical"
                    content={renderOpenGapsLegend()}
                  />
                </PieChart>
                <PieChart height={150} width={150}>
                  <Pie
                    cx="50%"
                    cy="50%"
                    data={totalGaps === 0 ? emptyData : pieData}
                    dataKey="value"
                    label={totalGaps !== 0 ? renderLabel : false}
                    labelLine={false}
                    nameKey="name"
                    shape={(shapeProps: PieSectorShapeProps) => <Sector {...shapeProps} fill={shapeProps.payload.color} />}
                  />
                  {totalGaps !== 0 && <Tooltip formatter={(value: number) => value.toLocaleString()} />}
                </PieChart>
              </div>
            </>
        }
      </QESWidgetContent>
    </QESWidget>
  );
};

export default ComplianceConnectOpenGapsWidget;
